import { useState, useEffect, useCallback } from "react";
import AdvancedFilterBar, { applyAdvancedFilters } from "../components/AdvancedFilterBar";
import EncaissementsFormDialog from "../components/EncaissementsFormDialog";
import EncaissementsImportDialog from "../components/EncaissementsImportDialog";
import { CompareToggle, Delta, AnomaliesAlert } from "../components/CompareToggle";
import PageHeader from "../components/PageHeader";
import { TableSkeleton } from "../components/Skeletons";
import { useAuth } from "../contexts/AuthContext";
import { useSociete } from "../contexts/SocieteContext";
import {
  Wallet, Plus, Loader2, Check, X, Filter, Calendar, ChevronDown,
  Pencil, Trash2, CheckCircle2, TrendingUp, DollarSign, BarChart3,
  ChevronLeft, ChevronRight, Users, FileText, Settings2, Search, Eye, EyeOff, GripVertical,
  Upload, FileSpreadsheet, ArrowRight
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Area, AreaChart } from "recharts";

const fmt = (v) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v || 0);

export default function EncaissementsPage() {
  const { axiosAuth } = useAuth();
  const { societeApi, currentSociete } = useSociete();
  const api = societeApi();

  const [items, setItems] = useState([]);
  const [config, setConfig] = useState({ financements: [], statuts: [] });
  const [summary, setSummary] = useState(null);
  const [months, setMonths] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("table");
  const [currentMonth, setCurrentMonth] = useState(new Date().toISOString().slice(0, 7));
  const [filterFin, setFilterFin] = useState("");
  const [filterStatut, setFilterStatut] = useState("");
  const [searchQ, setSearchQ] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [showConfig, setShowConfig] = useState(false);
  const [showInvoices, setShowInvoices] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [advFilters, setAdvFilters] = useState([]);
  const [filterLogic, setFilterLogic] = useState("AND");
  const [compareYoY, setCompareYoY] = useState(false);
  const [form, setForm] = useState({
    client_id: "", client_name: "", financement: "fonds_propres", statut_reglement: "en_cours",
    module_formation: "", total_ttc: "", regle: "", solde_cpf_opco: "", a_facturer: false,
    date_encaissement: "", date_encaissement_theorique: "", ref_devis: "", ref_facture: "", ref_doc: "",
    modalite_paiement: "", notes: "",
  });
  const [saving, setSaving] = useState(false);

  const monthLabel = (m) => {
    try { return new Date(m + "-01").toLocaleDateString("fr-FR", { month: "long", year: "numeric" }); }
    catch { return m; }
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [itemsR, configR, summaryR, monthsR] = await Promise.all([
        api.get(`/encaissements/list?month=${currentMonth}&financement=${filterFin}&statut=${filterStatut}`),
        api.get("/encaissements/config"),
        api.get(`/encaissements/summary?month=${currentMonth}${compareYoY ? "&compare=year" : ""}`),
        api.get("/encaissements/months"),
      ]);
      setItems(itemsR.data || []);
      setConfig(configR.data || { financements: [], statuts: [], columns: [] });
      setSummary(summaryR.data || null);
      setMonths(monthsR.data || []);
    } catch (e) { console.error("Encaissements load error:", e); }
    setLoading(false);
  }, [currentMonth, filterFin, filterStatut, compareYoY, currentSociete]); // reload on société switch

  useEffect(() => { loadData(); }, [loadData]);

  const resetForm = () => {
    setForm({ client_id: "", client_name: "", financement: "fonds_propres", statut_reglement: "en_cours",
      module_formation: "", total_ttc: "", regle: "", solde_cpf_opco: "", a_facturer: false,
      date_encaissement: "", date_encaissement_theorique: "", ref_devis: "", ref_facture: "", ref_doc: "",
      modalite_paiement: "", notes: "" });
    setEditItem(null); setShowForm(false);
  };

  const startEdit = (item) => {
    setForm({
      client_id: item.client_id || "", client_name: item.client_name || "",
      financement: item.financement || "fonds_propres", statut_reglement: item.statut_reglement || "en_cours",
      module_formation: item.module_formation || "", total_ttc: item.total_ttc || "",
      regle: item.regle || "", solde_cpf_opco: item.solde_cpf_opco || "", a_facturer: !!item.a_facturer,
      date_encaissement: item.date_encaissement || "", date_encaissement_theorique: item.date_encaissement_theorique || "",
      ref_devis: item.ref_devis || "", ref_facture: item.ref_facture || "", ref_doc: item.ref_doc || "",
      modalite_paiement: item.modalite_paiement || "", notes: item.notes || "",
    });
    setEditItem(item); setShowForm(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editItem) {
        await api.put(`/encaissements/${editItem.encaissement_id}`, { ...form, period: currentMonth });
      } else {
        await api.post("/encaissements/create", { ...form, period: currentMonth });
      }
      resetForm(); loadData();
    } catch { /* ignore */ }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/encaissements/${id}`); loadData(); } catch { /* ignore */ }
  };

  const loadInvoices = async () => {
    try { const r = await api.get("/encaissements/from-invoices"); setInvoices(r.data || []); setShowInvoices(true); } catch { /* ignore */ }
  };

  const createFromInvoice = (inv) => {
    setForm({
      ...form, client_id: inv.client_id, client_name: inv.client_name,
      total_ttc: inv.total_ttc, ref_facture: inv.type === "facture" ? inv.ref : "",
      ref_devis: inv.type === "devis" ? inv.ref : "",
      date_encaissement_theorique: inv.due_date || "",
    });
    setShowInvoices(false); setShowForm(true);
  };

  const filtered = applyAdvancedFilters(
    items.filter(i => !searchQ || (i.client_name || "").toLowerCase().includes(searchQ.toLowerCase()) || (i.ref_facture || "").toLowerCase().includes(searchQ.toLowerCase()) || (i.ref_doc || "").toLowerCase().includes(searchQ.toLowerCase())),
    advFilters, filterLogic, config
  );

  const getFinLabel = (k) => config.financements.find(f => f.key === k)?.label || k;
  const getFinColor = (k) => config.financements.find(f => f.key === k)?.color || "#94a3b8";
  const getStatLabel = (k) => config.statuts.find(s => s.key === k)?.label || k;
  const getStatColor = (k) => config.statuts.find(s => s.key === k)?.color || "#94a3b8";

  const visibleCols = (config.columns || []).filter(c => c.visible);

  const updateField = async (encId, field, value) => {
    try { await api.put(`/encaissements/${encId}`, { [field]: value }); loadData(); } catch { /* ignore */ }
  };

  const renderCell = (item, col) => {
    const val = item[col.key];
    switch (col.type) {
      case "currency":
        if (col.key === "reste_a_regler") return <span className="text-xs font-bold" style={{ color: val > 0 ? "#ef4444" : "#22c55e" }}>{fmt(val)}</span>;
        if (col.key === "regle") return <span className="text-xs text-green-400">{fmt(val)}</span>;
        if (col.key === "solde_cpf_opco") return <span className="text-xs" style={{ color: "#8b5cf6" }}>{fmt(val)}</span>;
        return <span className="text-xs font-bold" style={{ color: "#f7c935" }}>{fmt(val)}</span>;
      case "date":
        return <span className="text-[10px]" style={{ color: "var(--text-muted)" }}>{val || "-"}</span>;
      case "checkbox":
        return <input type="checkbox" checked={!!val} onChange={() => updateField(item.encaissement_id, col.key, !val)} className="w-4 h-4 accent-[#f7c935] cursor-pointer" />;
      case "select":
        if (col.source === "financements") return <span className="text-[10px] px-2 py-0.5 rounded-full font-bold" style={{ background: getFinColor(val) + "20", color: getFinColor(val) }}>{getFinLabel(val)}</span>;
        if (col.source === "statuts") return <span className="text-[10px] px-2 py-0.5 rounded-full font-bold" style={{ background: getStatColor(val) + "20", color: getStatColor(val) }}>{getStatLabel(val)}</span>;
        return <span className="text-xs" style={{ color: "var(--text-primary)" }}>{val || "-"}</span>;
      default:
        if (col.key === "ref_doc") return <span className="text-[10px]" style={{ color: "var(--text-muted)" }}>{item.ref_doc || item.ref_facture || item.ref_devis || "-"}</span>;
        return <span className="text-xs" style={{ color: "var(--text-primary)" }}>{val || "-"}</span>;
    }
  };

  return (
    <div className="p-6 max-w-full" data-testid="encaissements-page">
      <PageHeader
        icon={<DollarSign />}
        title="Encaissements"
        description="Suivi des règlements"
        testId="encaissements-header"
        actions={(
          <>
            <Button onClick={() => setShowImport(true)} variant="outline" className="border-white/10 text-white hover:bg-white/5 text-xs h-9" data-testid="import-enc-btn">
              <Upload className="w-3.5 h-3.5 mr-1.5" /> Importer
            </Button>
            <Button onClick={loadInvoices} variant="outline" className="border-white/10 text-white hover:bg-white/5 text-xs h-9" data-testid="from-invoices-btn">
              <FileText className="w-3.5 h-3.5 mr-1.5" /> Depuis factures/devis
            </Button>
            <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold text-xs h-9" data-testid="add-encaissement-btn">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Nouvel encaissement
            </Button>
            <button onClick={() => setShowConfig(true)} className="p-2 rounded-lg hover:bg-white/5" data-testid="config-btn">
              <Settings2 className="w-4 h-4" style={{ color: "var(--text-muted)" }} />
            </button>
          </>
        )}
      />

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {/* Month nav */}
        <div className="flex items-center gap-1 rounded-lg border px-2 py-1" style={{ borderColor: "var(--border-primary)" }}>
          <button data-testid="enc-month-prev" onClick={() => { const [y, m] = currentMonth.split("-").map(Number); const nm = m === 1 ? 12 : m - 1; const ny = m === 1 ? y - 1 : y; setCurrentMonth(`${ny}-${String(nm).padStart(2, "0")}`); }}>
            <ChevronLeft className="w-4 h-4" style={{ color: "var(--text-muted)" }} />
          </button>
          <span className="text-xs font-bold px-2 capitalize min-w-[120px] text-center" style={{ color: "var(--text-primary)" }}>{monthLabel(currentMonth)}</span>
          <button data-testid="enc-month-next" onClick={() => { const [y, m] = currentMonth.split("-").map(Number); const nm = m === 12 ? 1 : m + 1; const ny = m === 12 ? y + 1 : y; setCurrentMonth(`${ny}-${String(nm).padStart(2, "0")}`); }}>
            <ChevronRight className="w-4 h-4" style={{ color: "var(--text-muted)" }} />
          </button>
        </div>

        <Select value={filterFin || "__all__"} onValueChange={v => setFilterFin(v === "__all__" ? "" : v)}>
          <SelectTrigger data-testid="filter-financement" className="h-8 w-auto min-w-[160px] bg-transparent border text-xs" style={{ borderColor: "var(--border-primary)", color: "var(--text-primary)" }}>
            <SelectValue placeholder="Tous financements" />
          </SelectTrigger>
          <SelectContent className="bg-[#14101c] border-white/10 text-white max-h-72">
            <SelectItem value="__all__" className="text-xs text-slate-200 focus:bg-white/10 focus:text-white" data-testid="filter-financement-option-all">Tous financements</SelectItem>
            {config.financements.map(f => (
              <SelectItem key={f.key} value={f.key} className="text-xs text-slate-200 focus:bg-white/10 focus:text-white" data-testid={`filter-financement-option-${f.key}`}>{f.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={filterStatut || "__all__"} onValueChange={v => setFilterStatut(v === "__all__" ? "" : v)}>
          <SelectTrigger data-testid="filter-statut" className="h-8 w-auto min-w-[140px] bg-transparent border text-xs" style={{ borderColor: "var(--border-primary)", color: "var(--text-primary)" }}>
            <SelectValue placeholder="Tous statuts" />
          </SelectTrigger>
          <SelectContent className="bg-[#14101c] border-white/10 text-white max-h-72">
            <SelectItem value="__all__" className="text-xs text-slate-200 focus:bg-white/10 focus:text-white" data-testid="filter-statut-option-all">Tous statuts</SelectItem>
            {config.statuts.map(s => (
              <SelectItem key={s.key} value={s.key} className="text-xs text-slate-200 focus:bg-white/10 focus:text-white" data-testid={`filter-statut-option-${s.key}`}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="relative">
          <Search className="absolute left-2 top-2 w-3.5 h-3.5" style={{ color: "var(--text-muted)" }} />
          <input value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="Rechercher..."
            className="h-8 bg-transparent border rounded-lg pl-7 pr-3 text-xs outline-none w-48" style={{ borderColor: "var(--border-primary)", color: "var(--text-primary)" }} data-testid="search-enc" />
        </div>

        <div className="flex items-center gap-1 rounded-lg border px-1 py-1 ml-auto" style={{ borderColor: "var(--border-primary)" }}>
          {[{ k: "table", l: "Tableau" }, { k: "summary", l: "Synthese" }].map(v => (
            <button key={v.k} onClick={() => setView(v.k)} data-testid={`view-${v.k}`}
              className={`px-4 py-1.5 rounded-md text-[11px] font-bold transition-colors ${view === v.k ? "text-[#0b0811]" : ""}`}
              style={view === v.k ? { background: "#f7c935" } : { color: "var(--text-muted)" }}>
              {v.l}
            </button>
          ))}
        </div>
      </div>

      {/* Summary KPI cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
          {[
            { label: "Total TTC", val: fmt(summary.total_ttc), icon: DollarSign, color: "#f7c935" },
            { label: "Regle", val: fmt(summary.total_regle), icon: CheckCircle2, color: "#22c55e" },
            { label: "CPF/OPCO", val: fmt(summary.total_cpf_opco), icon: Wallet, color: "#8b5cf6" },
            { label: "Reste a regler", val: fmt(summary.total_reste), icon: TrendingUp, color: summary.total_reste > 0 ? "#ef4444" : "#22c55e" },
            { label: "Encaissements", val: summary.count, icon: BarChart3, color: "#3b82f6" },
          ].map((c, i) => (
            <div key={i} className="rounded-xl border p-4" style={{ background: "var(--bg-card)", borderColor: "var(--border-primary)" }}>
              <div className="flex items-center gap-2 mb-2">
                <c.icon className="w-4 h-4" style={{ color: c.color }} />
                <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: "var(--text-muted)" }}>{c.label}</span>
              </div>
              <div className="text-lg font-black" style={{ color: "var(--text-primary)" }}>{c.val}</div>
            </div>
          ))}
        </div>
      )}

      {/* Advanced Filter Bar */}
      <AdvancedFilterBar columns={config.columns || []} config={config} filters={advFilters} setFilters={setAdvFilters} logic={filterLogic} setLogic={setFilterLogic} />

      {/* Main content */}
      {loading ? (
        <TableSkeleton rows={6} cols={5} testId="encaissements-loading-skeleton" />
      ) : view === "summary" ? (
        <div className="space-y-4" data-testid="summary-view">
          {summary?.anomalies && summary.anomalies.length > 0 && (
            <AnomaliesAlert anomalies={summary.anomalies} testId="encaissements-anomalies" />
          )}
          {/* Header avec toggle YoY */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <h2 className="text-sm font-bold" style={{ color: "var(--text-primary)" }}>Synthese — {monthLabel(currentMonth)}</h2>
            <div className="flex items-center gap-2">
              <CompareToggle enabled={compareYoY} onChange={setCompareYoY} testId="encaissements-compare-yoy" />
              {compareYoY && summary?.prev && (
                <span className="text-[10px] font-bold tabular-nums" style={{ color: "var(--text-muted)" }} data-testid="encaissements-yoy-total">
                  N-1: {fmt(summary.prev.total_ttc)}
                  <span className="ml-2"><Delta current={summary.total_ttc} previous={summary.prev.total_ttc} testId="encaissements-yoy-total-delta" /></span>
                </span>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-xl border p-5" style={{ background: "var(--bg-card)", borderColor: "var(--border-primary)" }}>
              <h3 className="text-xs font-bold mb-3" style={{ color: "var(--text-primary)" }}>Par financement</h3>
              {summary && Object.entries(summary.by_financement).map(([k, v]) => {
                const prevV = summary.prev?.by_financement?.[k];
                return (
                  <div key={k} className="flex items-center justify-between py-2 border-b" style={{ borderColor: "rgba(255,255,255,0.03)" }}>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full" style={{ background: getFinColor(k) }} />
                      <span className="text-xs" style={{ color: "var(--text-primary)" }}>{getFinLabel(k)}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/5" style={{ color: "var(--text-muted)" }}>{v.count}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-xs font-bold" style={{ color: "#f7c935" }}>{fmt(v.total)}</div>
                      {compareYoY && <Delta current={v.total} previous={prevV?.total || 0} testId={`encaissements-yoy-fin-${k}`} />}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="rounded-xl border p-5" style={{ background: "var(--bg-card)", borderColor: "var(--border-primary)" }}>
              <h3 className="text-xs font-bold mb-3" style={{ color: "var(--text-primary)" }}>Par statut</h3>
              {summary && Object.entries(summary.by_statut).map(([k, v]) => {
                const prevV = summary.prev?.by_statut?.[k];
                return (
                  <div key={k} className="flex items-center justify-between py-2 border-b" style={{ borderColor: "rgba(255,255,255,0.03)" }}>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full" style={{ background: getStatColor(k) }} />
                      <span className="text-xs" style={{ color: "var(--text-primary)" }}>{getStatLabel(k)}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/5" style={{ color: "var(--text-muted)" }}>{v.count}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-xs font-bold" style={{ color: "#f7c935" }}>{fmt(v.total)}</div>
                      {compareYoY && <Delta current={v.total} previous={prevV?.total || 0} testId={`encaissements-yoy-stat-${k}`} />}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        /* Table view - dynamic columns */
        <div className="rounded-xl border overflow-hidden" style={{ background: "var(--bg-card)", borderColor: "var(--border-primary)" }} data-testid="table-view">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b" style={{ borderColor: "var(--border-primary)" }}>
                  {visibleCols.map(col => (
                    <th key={col.key} className={`px-3 py-2.5 text-[10px] font-medium uppercase tracking-wider ${col.type === "currency" ? "text-right" : col.type === "checkbox" ? "text-center" : ""}`} style={{ color: "var(--text-muted)" }}>
                      {col.label}
                    </th>
                  ))}
                  <th className="px-3 py-2.5 text-[10px] font-medium uppercase tracking-wider text-right" style={{ color: "var(--text-muted)" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={visibleCols.length + 1} className="text-center py-12 text-sm" style={{ color: "var(--text-muted)" }}>Aucun encaissement pour cette periode</td></tr>
                ) : filtered.map(item => (
                  <tr key={item.encaissement_id} className="border-b hover:bg-white/[0.02]" style={{ borderColor: "rgba(255,255,255,0.03)" }}>
                    {visibleCols.map(col => (
                      <td key={col.key} className={`px-3 py-2.5 ${col.type === "currency" ? "text-right" : col.type === "checkbox" ? "text-center" : ""}`}>
                        {renderCell(item, col)}
                      </td>
                    ))}
                    <td className="px-3 py-2.5 text-right">
                      <div className="flex items-center gap-1 justify-end">
                        <button onClick={() => startEdit(item)} className="p-1.5 rounded-lg hover:bg-white/10"><Pencil className="w-3 h-3" style={{ color: "var(--text-muted)" }} /></button>
                        <button onClick={() => handleDelete(item.encaissement_id)} className="p-1.5 rounded-lg hover:bg-red-500/10"><Trash2 className="w-3 h-3 text-red-400" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <EncaissementsFormDialog
        open={showForm} onClose={resetForm} editItem={editItem}
        form={form} setForm={setForm} onSave={handleSave} saving={saving} config={config}
      />

      {/* From Invoices Dialog */}
      <Dialog open={showInvoices} onOpenChange={setShowInvoices}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="invoices-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">Factures et devis en attente</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 mt-3">
            {invoices.length === 0 ? (
              <p className="text-sm text-center py-8" style={{ color: "var(--text-muted)" }}>Aucune facture ou devis en attente</p>
            ) : invoices.map((inv, i) => (
              <button key={i} onClick={() => createFromInvoice(inv)} className="w-full flex items-center justify-between p-3 rounded-lg border hover:border-[#f7c935]/30 transition-colors text-left" style={{ borderColor: "var(--border-primary)", background: "var(--bg-card)" }}>
                <div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${inv.type === "facture" ? "bg-blue-500/20 text-blue-400" : "bg-amber-500/20 text-amber-400"}`}>{inv.type === "facture" ? "Facture" : "Devis"}</span>
                    <span className="text-xs font-bold" style={{ color: "var(--text-primary)" }}>{inv.ref || "Sans ref"}</span>
                  </div>
                  <span className="text-[10px] mt-1 block" style={{ color: "var(--text-muted)" }}>{inv.client_name || "Client inconnu"}</span>
                </div>
                <div className="text-right">
                  <div className="text-sm font-bold" style={{ color: "#f7c935" }}>{fmt(inv.total_ttc)}</div>
                </div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <EncaissementsImportDialog
        open={showImport} onClose={() => setShowImport(false)}
        onImport={async (file) => {
          const formData = new FormData();
          formData.append("file", file);
          formData.append("period", currentMonth);
          const r = await api.post("/encaissements/import-file", formData, { headers: { "Content-Type": "multipart/form-data" } });
          loadData();
          return r.data;
        }}
        monthLabel={monthLabel(currentMonth)}
      />

      {/* Config Dialog */}
      <Dialog open={showConfig} onOpenChange={setShowConfig}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-lg max-h-[80vh] overflow-y-auto" data-testid="config-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">Configuration Encaissements</DialogTitle>
          </DialogHeader>
          <ConfigPanel config={config} api={api} onSave={() => { setShowConfig(false); loadData(); }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ConfigPanel({ config, api, onSave }) {
  const [financements, setFinancements] = useState(config.financements || []);
  const [statuts, setStatuts] = useState(config.statuts || []);
  const [columns, setColumns] = useState(config.columns || []);
  const [newFin, setNewFin] = useState("");
  const [newStat, setNewStat] = useState("");
  const [newColName, setNewColName] = useState("");
  const [newColType, setNewColType] = useState("text");
  const [saving, setSaving] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  const addFin = () => { if (newFin.trim()) { setFinancements([...financements, { key: newFin.trim().toLowerCase().replace(/[^a-z0-9]/g, "_"), label: newFin.trim(), color: "#" + Math.floor(Math.random()*16777215).toString(16).padStart(6,"0") }]); setNewFin(""); } };
  const removeFin = (i) => setFinancements(financements.filter((_, idx) => idx !== i));
  const addStat = () => { if (newStat.trim()) { setStatuts([...statuts, { key: newStat.trim().toLowerCase().replace(/[^a-z0-9]/g, "_"), label: newStat.trim(), color: "#" + Math.floor(Math.random()*16777215).toString(16).padStart(6,"0") }]); setNewStat(""); } };
  const removeStat = (i) => setStatuts(statuts.filter((_, idx) => idx !== i));
  const toggleCol = (i) => { const c = [...columns]; c[i] = {...c[i], visible: !c[i].visible}; setColumns(c); };
  const removeCol = (i) => setColumns(columns.filter((_, idx) => idx !== i));
  const addCol = () => { if (newColName.trim()) { setColumns([...columns, { key: newColName.trim().toLowerCase().replace(/[^a-z0-9]/g,"_"), label: newColName.trim(), type: newColType, visible: true }]); setNewColName(""); setNewColType("text"); } };

  const handleDrop = (dropIdx) => {
    if (dragIdx === null || dragIdx === dropIdx) { setDragIdx(null); setDragOverIdx(null); return; }
    const reordered = [...columns];
    const [moved] = reordered.splice(dragIdx, 1);
    reordered.splice(dropIdx, 0, moved);
    setColumns(reordered);
    setDragIdx(null); setDragOverIdx(null);
  };

  const save = async () => { setSaving(true); try { await api.put("/encaissements/config", { financements, statuts, columns }); onSave(); } catch {} setSaving(false); };

  const CT = { text: "Texte", number: "Nombre", currency: "Montant", date: "Date", checkbox: "Case", select: "Liste" };

  return (
    <div className="space-y-5 mt-3">
      <div>
        <h3 className="text-xs font-bold mb-2" style={{ color: "#f7c935" }}>Colonnes du tableau</h3>
        <div className="space-y-1 mb-2 max-h-44 overflow-y-auto">
          {columns.map((col, i) => (
            <div key={col.key + i} draggable onDragStart={() => setDragIdx(i)} onDragOver={(e) => { e.preventDefault(); setDragOverIdx(i); }} onDrop={() => handleDrop(i)} onDragEnd={() => { setDragIdx(null); setDragOverIdx(null); }}
              className={`flex items-center gap-2 px-2 py-1.5 rounded-lg transition-all ${dragOverIdx === i ? "ring-1 ring-[#f7c935]/50 bg-[#f7c935]/5" : "bg-[#0b0811]"} ${dragIdx === i ? "opacity-40" : ""}`}
              style={{ cursor: "grab" }} data-testid={`col-row-${col.key}`}>
              <GripVertical className="w-3.5 h-3.5 text-slate-600 flex-shrink-0 cursor-grab" />
              <button onClick={() => toggleCol(i)} className="p-0.5 flex-shrink-0">{col.visible ? <Eye className="w-3.5 h-3.5 text-green-400" /> : <EyeOff className="w-3.5 h-3.5 text-slate-500" />}</button>
              <span className="text-xs flex-1" style={{ color: col.visible ? "var(--text-primary)" : "var(--text-dimmed)" }}>{col.label}</span>
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-white/5 text-slate-500">{CT[col.type] || col.type}</span>
              {!col.fixed && <button onClick={() => removeCol(i)} className="p-0.5 hover:bg-red-500/10 rounded"><X className="w-3 h-3 text-red-400" /></button>}
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <Input value={newColName} onChange={e => setNewColName(e.target.value)} onKeyDown={e => e.key === "Enter" && addCol()} placeholder="Nom colonne" className="bg-[#0b0811] border-white/10 text-white text-xs h-8 flex-1" />
          <select value={newColType} onChange={e => setNewColType(e.target.value)} className="bg-[#0b0811] border border-white/10 rounded-md text-xs h-8 px-2 text-white">{Object.entries(CT).map(([k,v]) => <option key={k} value={k}>{v}</option>)}</select>
          <Button onClick={addCol} disabled={!newColName.trim()} className="bg-[#f7c935] text-[#0b0811] font-bold text-xs h-8"><Plus className="w-3 h-3" /></Button>
        </div>
      </div>
      <div>
        <h3 className="text-xs font-bold mb-2" style={{ color: "#f7c935" }}>Types de financement</h3>
        <div className="space-y-1 mb-2 max-h-36 overflow-y-auto">
          {financements.map((f, i) => (<div key={i} className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-[#0b0811]"><div className="w-3 h-3 rounded-full flex-shrink-0" style={{background: f.color}} /><span className="text-xs flex-1" style={{color:"var(--text-primary)"}}>{f.label}</span><button onClick={() => removeFin(i)} className="p-0.5 hover:bg-red-500/10 rounded"><X className="w-3 h-3 text-red-400" /></button></div>))}
        </div>
        <div className="flex gap-2"><Input value={newFin} onChange={e => setNewFin(e.target.value)} onKeyDown={e => e.key === "Enter" && addFin()} placeholder="Nouveau type" className="bg-[#0b0811] border-white/10 text-white text-xs h-8 flex-1" /><Button onClick={addFin} disabled={!newFin.trim()} className="bg-[#f7c935] text-[#0b0811] font-bold text-xs h-8"><Plus className="w-3 h-3" /></Button></div>
      </div>
      <div>
        <h3 className="text-xs font-bold mb-2" style={{ color: "#f7c935" }}>Statuts de reglement</h3>
        <div className="space-y-1 mb-2 max-h-36 overflow-y-auto">
          {statuts.map((s, i) => (<div key={i} className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-[#0b0811]"><div className="w-3 h-3 rounded-full flex-shrink-0" style={{background: s.color}} /><span className="text-xs flex-1" style={{color:"var(--text-primary)"}}>{s.label}</span><button onClick={() => removeStat(i)} className="p-0.5 hover:bg-red-500/10 rounded"><X className="w-3 h-3 text-red-400" /></button></div>))}
        </div>
        <div className="flex gap-2"><Input value={newStat} onChange={e => setNewStat(e.target.value)} onKeyDown={e => e.key === "Enter" && addStat()} placeholder="Nouveau statut" className="bg-[#0b0811] border-white/10 text-white text-xs h-8 flex-1" /><Button onClick={addStat} disabled={!newStat.trim()} className="bg-[#f7c935] text-[#0b0811] font-bold text-xs h-8"><Plus className="w-3 h-3" /></Button></div>
      </div>
      <Button onClick={save} disabled={saving} className="w-full bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold" data-testid="save-config-btn">
        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />} Enregistrer la configuration
      </Button>
    </div>
  );
}
